<script lang="ts">
    import { beforeUpdate, createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { offset, flip, shift } from 'svelte-floating-ui/dom';
    import { createFloatingActions } from 'svelte-floating-ui';
    import type { Placement } from '@floating-ui/dom';
    import { _ } from 'svelte-i18n';

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface $$Slots {
        'list-prepend': Record<string, never>;
        list: { filteredItems: any[] };
        'list-append': Record<string, never>;
        item: { item: any; index: number; checked: any };
        empty: Record<string, never>;
        selection: { selection: any; index?: number; closeList: () => void };
        prepend: Record<string, never>;
        'loading-icon': Record<string, never>;
        'clear-icon': Record<string, never>;
        'chevron-icon': { listOpen: boolean };
        'input-hidden': { value: any };
        required: { value: any };
    }

    const dispatch = createEventDispatcher();

    import _filter from './filter';
    import _getItems from './get-items';

    import LoadingIcon from './LoadingIcon.svelte';
    import { twMerge } from 'tailwind-merge';
    import { isEqual } from 'lodash-es';
    import { Badge, Checkbox } from '../..';

    export let justValue = null; // read-only

    export let filter = _filter;
    export let getItems = _getItems;

    export let id: string | null = null;
    export let name: string | null = null;
    export let container: HTMLElement | undefined = undefined;
    export let input: HTMLInputElement | undefined = undefined;
    export let multiple = false;
    export let multiFullItemClearable = false;
    export let disabled = false;
    export let focused = false;
    export let value: any = null;
    export let filterText = '';
    export let placeholder = $_('packages.components.lib.basic.forms.svelteSelect.select.placeholder');
    export let placeholderAlwaysShow = false;
    export let items: any = null;
    export let label = 'label';
    export let itemFilter = (label, filterText, option) => `${label}`.toLowerCase().includes(filterText.toLowerCase());
    export let groupBy: ((item: any) => string | undefined) | undefined = undefined;
    export let groupFilter = groups => groups;
    export let groupHeaderSelectable = false;
    export let itemId = 'value';
    export let loadOptions: any = undefined;
    export let containerStyles = '';
    export let hasError = false;
    export let filterSelectedItems = true;
    export let required = false;
    export let closeListOnChange = true;
    export let closeListOnBlur = true;
    export let clearFilterTextOnBlur = true;

    export let creatable = false;
    export let dismissCriteria: ((a, b) => boolean) | undefined = undefined;
    export let color: 'red' | 'base' = 'base';
    export let checkable = false;
    export let size: 'md' | 'sm' = 'md';

    export let createGroupHeaderItem = (groupValue, item) => {
        return {
            value: groupValue,
            [label]: groupValue,
        };
    };

    export const getFilteredItems = () => {
        return filteredItems;
    };

    export let searchable = true;
    export let inputStyles = '';
    export let clearable = true;
    export let loading = false;
    export let listOpen = false;

    let timeout;
    export let debounce = (fn, wait = 1) => {
        clearTimeout(timeout);
        timeout = setTimeout(fn, wait);
    };

    export let debounceWait = 300;
    export let hideEmptyState = false;
    export let inputAttributes = { class: 'disabled:!cursor-not-allowed hover:!cursor-pointer' };
    export let listAutoWidth = true;
    export let showChevron = false;
    export let listOffset = 8;
    export let hoverItemIndex = 0;
    export let floatingConfig = {};
    export let hideList = false; // 리스트 완전히 숨기기

    export let strategy = 'absolute';
    export let placement = 'bottom-start';

    let activeValue;
    let prev_value;
    let prev_filterText;
    let prev_multiple;

    function setValue() {
        if (typeof value === 'string') {
            let item = (items || []).find(item => item[itemId] === value);
            value = item || {
                [itemId]: value,
                label: value,
            };
        } else if (multiple && Array.isArray(value) && value.length > 0) {
            value = value.map(item => (typeof item === 'string' ? { value: item, label: item } : item));
        }
    }

    let _inputAttributes;
    function assignInputAttributes() {
        _inputAttributes = Object.assign(
            {
                autocapitalize: 'none',
                autocomplete: 'off',
                autocorrect: 'off',
                spellcheck: false,
                tabindex: 0,
                type: 'text',
                'aria-autocomplete': 'list',
            },
            inputAttributes,
        );

        if (id) {
            _inputAttributes['id'] = id;
        }

        if (!searchable) {
            _inputAttributes['readonly'] = true;
        }
    }

    function convertStringItemsToObjects(_items) {
        return _items.map((item, index) => {
            return {
                index,
                value: item,
                label: `${item}`,
            };
        });
    }

    function filterGroupedItems(_items: any[]) {
        const groupValues: any[] = [];
        const groups: Record<string, any[]> = {};

        _items.forEach(item => {
            const groupValue = groupBy?.(item);
            const groupKey = groupValue ?? '';

            if (!groupValues.includes(groupValue)) {
                groupValues.push(groupValue);
                groups[groupKey] = [];

                if (groupValue) {
                    groups[groupKey].push(
                        Object.assign(createGroupHeaderItem(groupValue, item), {
                            id: groupValue,
                            groupHeader: true,
                            selectable: groupHeaderSelectable,
                        }),
                    );
                }
            }

            groups[groupKey].push(Object.assign({ groupItem: !!groupValue }, item));
        });

        const sortedGroupedItems: any[] = [];

        groupFilter(groupValues).forEach(groupValue => {
            if (groups[groupValue]) sortedGroupedItems.push(...groups[groupValue]);
        });

        return sortedGroupedItems;
    }

    function dispatchSelectedItem() {
        if (multiple) {
            if (JSON.stringify(value) !== JSON.stringify(prev_value)) {
                if (checkValueForDuplicates()) {
                    dispatch('input', value);
                }
            }
            return;
        }

        if (!prev_value || JSON.stringify(value[itemId]) !== JSON.stringify(prev_value[itemId])) {
            dispatch('input', value);
        }
    }

    function setupMulti() {
        if (value) {
            if (Array.isArray(value)) {
                value = [...value];
            } else {
                value = [value];
            }
        }
    }

    function setupSingle() {
        if (value) value = null;
    }

    $: if ((items, value)) setValue();
    $: if (inputAttributes || !searchable) assignInputAttributes();
    $: if (multiple) setupMulti();
    $: if (prev_multiple && !multiple) setupSingle();
    $: if (multiple && value && value.length > 1) checkValueForDuplicates();
    $: if (value) dispatchSelectedItem();
    $: if (!value && multiple && prev_value) dispatch('input', value);
    $: if (closeListOnBlur && !focused && input) closeList();
    $: if (filterText !== prev_filterText) setupFilterText();
    $: if (!multiple && listOpen && value && filteredItems) setValueIndexAsHoverIndex();
    $: dispatchHover(hoverItemIndex);

    function setValueIndexAsHoverIndex() {
        const valueIndex = filteredItems.findIndex(i => {
            return i[itemId] === value[itemId];
        });

        checkHoverSelectable(valueIndex, true);
    }

    function dispatchHover(i) {
        dispatch('hoverItem', i);
    }

    function checkHoverSelectable(startingIndex = 0, ignoreGroup?: any) {
        hoverItemIndex = startingIndex < 0 ? 0 : startingIndex;
        if (!ignoreGroup && groupBy && filteredItems[hoverItemIndex] && !filteredItems[hoverItemIndex].selectable) {
            setHoverIndex(1);
        }
    }

    function setupFilterText() {
        if (!loadOptions && filterText.length === 0) return;

        if (loadOptions) {
            debounce(async function () {
                loading = true;
                let res = await getItems({
                    dispatch,
                    loadOptions,
                    convertStringItemsToObjects,
                    filterText,
                });

                if (res) {
                    loading = res.loading;
                    listOpen = listOpen ? res.listOpen : filterText.length > 0 ? true : false;
                    focused = listOpen && res.focused;
                    items = groupBy ? filterGroupedItems(res.filteredItems) : res.filteredItems;
                } else {
                    loading = false;
                    focused = true;
                    listOpen = true;
                }
            }, debounceWait);
        } else {
            listOpen = true;

            if (multiple) {
                activeValue = undefined;
            }
        }
    }

    $: hasValue = multiple ? value && value.length > 0 : value;
    $: hideSelectedItem = hasValue && filterText.length > 0;
    $: showClear = hasValue && clearable && !disabled && !loading;
    $: placeholderText = placeholderAlwaysShow && multiple ? placeholder : multiple && value?.length === 0 ? placeholder : value ? '' : placeholder;
    $: ariaSelection = value ? handleAriaSelection(multiple) : '';
    $: ariaContext = handleAriaContent();
    $: updateValueDisplay(items);
    $: justValue = computeJustValue(multiple, value, itemId);
    $: if (!multiple && prev_value && !value) dispatch('input', value);
    $: filteredItems = filter({
        loadOptions,
        filterText,
        items,
        multiple,
        value,
        itemId,
        groupBy,
        label,
        filterSelectedItems,
        itemFilter,
        convertStringItemsToObjects,
        filterGroupedItems,
    });
    $: if (listOpen && filteredItems && !multiple && !value) checkHoverSelectable();
    $: handleFilterEvent(filteredItems);
    $: if (container && floatingConfig) floatingUpdate(Object.assign(_floatingConfig, floatingConfig) as any);
    $: listDom = !!list;
    $: listMounted(list, listOpen);
    $: if (listOpen && container && list) setListWidth();
    $: scrollToHoverItem = hoverItemIndex;
    $: if (listOpen && multiple) hoverItemIndex = 0;
    $: if (input && listOpen && !focused) handleFocus();
    $: if (filterText) hoverItemIndex = 0;

    function handleFilterEvent(items) {
        if (listOpen) {
            dispatch('filter', items);
            if (items.length === 0 && filterText && filterText.length > 0 && creatable) {
                const prev = items.filter(i => !i.created);
                items = [...prev, { value: filterText, label: filterText, created: true }];
            }
        }
    }

    beforeUpdate(async () => {
        prev_value = value;
        prev_filterText = filterText;
        prev_multiple = multiple;
    });

    function computeJustValue(_multiple?: boolean, _value?: any, _itemId?: string) {
        if (multiple) return value ? value.map((item: any) => item[itemId]) : null;
        return value ? value[itemId] : value;
    }

    function checkValueForDuplicates() {
        let noDuplicates = true;
        if (value) {
            const ids: any[] = [];
            const uniqueValues: any[] = [];

            value.forEach((val: any) => {
                if (!ids.includes(val[itemId])) {
                    ids.push(val[itemId]);
                    uniqueValues.push(val);
                } else {
                    noDuplicates = false;
                }
            });

            if (!noDuplicates) value = uniqueValues;
        }
        return noDuplicates;
    }

    function findItem(selection) {
        let matchTo = selection ? selection[itemId] : value[itemId];
        return items.find(item => item[itemId] === matchTo);
    }

    function updateValueDisplay(items) {
        if (!items || items.length === 0 || items.some(item => typeof item !== 'object')) return;
        if (!value || (multiple ? value.some(selection => !selection || !selection[itemId]) : !value[itemId])) return;

        if (Array.isArray(value)) {
            value = value.map(selection => findItem(selection) || selection);
        } else {
            value = findItem(value) || value;
        }
    }

    async function handleMultiItemClear(i) {
        const itemToRemove = value[i];

        if (value.length === 1) {
            value = [];
        } else {
            value = value.filter(item => {
                return item !== itemToRemove;
            });
        }

        dispatch('clear', itemToRemove);
        dispatch('change', value);
    }

    function handleKeyDown(e) {
        if (!focused) return;
        e.stopPropagation();
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                closeList();
                break;
            case 'Enter':
                // IME 입력 중에는 Enter 키 처리를 건너뜀 (한글 입력 시 중복 처리 방지)
                if (e.isComposing === true) return;

                e.preventDefault();

                if (listOpen) {
                    if (filteredItems.length === 0) break;
                    const hoverItem = filteredItems[hoverItemIndex];

                    if (value && !multiple && value[itemId] === hoverItem[itemId]) {
                        closeList();
                        break;
                    } else {
                        handleSelect(filteredItems[hoverItemIndex]);
                    }
                }

                break;
            case 'ArrowDown':
                e.preventDefault();

                if (listOpen) {
                    setHoverIndex(1);
                } else {
                    listOpen = true;
                    activeValue = undefined;
                }

                break;
            case 'ArrowUp':
                e.preventDefault();

                if (listOpen) {
                    setHoverIndex(-1);
                } else {
                    listOpen = true;
                    activeValue = undefined;
                }

                break;
            case 'Tab':
                // IME 입력 중에는 Tab 키 처리를 건너뜀
                if (e.isComposing === true) return;

                if (listOpen && focused) {
                    if (filteredItems.length === 0 || (value && value[itemId] === filteredItems[hoverItemIndex][itemId])) return closeList();

                    e.preventDefault();
                    handleSelect(filteredItems[hoverItemIndex]);
                    closeList();
                }

                break;
            case 'Backspace':
                if (!multiple || filterText.length > 0) return;

                if (multiple && value && value.length > 0) {
                    handleMultiItemClear(activeValue !== undefined ? activeValue : value.length - 1);
                    if (activeValue === 0 || activeValue === undefined) break;
                    activeValue = value.length > activeValue ? activeValue - 1 : undefined;
                }

                break;
            case 'ArrowLeft':
                if (!value || !multiple || filterText.length > 0) return;
                if (activeValue === undefined) {
                    activeValue = value.length - 1;
                } else if (value.length > activeValue && activeValue !== 0) {
                    activeValue -= 1;
                }
                break;
            case 'ArrowRight':
                if (!value || !multiple || filterText.length > 0 || activeValue === undefined) return;
                if (activeValue === value.length - 1) {
                    activeValue = undefined;
                } else if (activeValue < value.length - 1) {
                    activeValue += 1;
                }
                break;
        }
    }

    function handleFocus(e?: FocusEvent) {
        if (focused && input === document?.activeElement) return;
        if (e) dispatch('focus', e);
        input?.focus();
        focused = true;
    }

    async function handleBlur(e?: FocusEvent) {
        if (isScrolling) return;
        if (listOpen || focused) {
            dispatch('blur', e);
            if (closeListOnBlur) closeList();
            focused = false;
            activeValue = undefined;
            input?.blur();
        }
    }

    function handleClick() {
        if (disabled) return;
        if (filterText.length > 0) return (listOpen = true);
        listOpen = !listOpen;
    }

    export function handleClear() {
        dispatch('clear', value);
        if (multiple) {
            value = [];
        } else {
            value = undefined;
        }
        dispatch('change', value);
        closeList();
        handleFocus();
    }

    onMount(() => {
        if (listOpen) focused = true;
        if (focused && input) input.focus();
    });

    function itemSelected(selection) {
        if (selection) {
            filterText = '';
            const item = Object.assign({}, selection);

            if (item.groupHeader && !item.selectable) return;

            if (multiple) {
                if (checkable) {
                    const index = value?.findIndex(v => isEqual(v, item)) ?? -1;
                    if (index > -1) {
                        value = value.filter(v => !isEqual(v, item));
                    } else {
                        value = value ? value.concat([item]) : [item];
                    }
                } else {
                    value = value ? value.concat([item]) : [item];
                }
            } else {
                value = item;
            }

            setTimeout(() => {
                if (closeListOnChange) closeList();
                activeValue = undefined;
                dispatch('change', value);
                dispatch('select', selection);
            });
        }
    }

    function closeList() {
        if (clearFilterTextOnBlur) {
            filterText = '';
        }
        listOpen = false;
    }

    export let ariaValues = values => {
        return `Option ${values}, selected.`;
    };

    export let ariaListOpen = (label, count) => {
        return `You are currently focused on option ${label}. There are ${count} results available.`;
    };

    export let ariaFocused = () => {
        return `Select is focused, type to refine list, press down to open the menu.`;
    };

    function handleAriaSelection(_multiple) {
        let selected = undefined;

        if (_multiple && value.length > 0) {
            selected = value.map(v => v[label]).join(', ');
        } else {
            selected = value[label];
        }

        return ariaValues(selected);
    }

    function handleAriaContent() {
        if (!filteredItems || filteredItems.length === 0) return '';
        let _item = filteredItems[hoverItemIndex];
        if (listOpen && _item) {
            let count = filteredItems ? filteredItems.length : 0;
            return ariaListOpen(_item[label], count);
        } else {
            return ariaFocused();
        }
    }

    let list: HTMLElement | undefined = undefined;

    let isScrollingTimer;
    function handleListScroll() {
        clearTimeout(isScrollingTimer);
        isScrollingTimer = setTimeout(() => {
            isScrolling = false;
        }, 100);
    }

    function handleClickOutside(event) {
        if (!listOpen && !focused && container && !container.contains(event.target) && !list?.contains(event.target)) {
            handleBlur();
        }
    }

    onDestroy(() => {
        list?.remove();
    });

    let isScrolling = false;

    function handleSelect(item) {
        if (!item || item.selectable === false) return;
        itemSelected(item);
    }

    function handleHover(i) {
        if (isScrolling) return;
        hoverItemIndex = i;
    }

    function handleItemClick(args) {
        const { item, i } = args;
        if (item?.selectable === false) return;
        if (value && !multiple && value[itemId] === item[itemId]) return closeList();
        if (isItemSelectable(item)) {
            hoverItemIndex = i;
            handleSelect(item);
        }
    }

    function setHoverIndex(increment) {
        let selectableFilteredItems = filteredItems.filter(item => !Object.hasOwn(item, 'selectable') || item.selectable === true);

        if (selectableFilteredItems.length === 0) {
            return (hoverItemIndex = 0);
        }

        if (increment > 0 && hoverItemIndex === filteredItems.length - 1) {
            hoverItemIndex = 0;
        } else if (increment < 0 && hoverItemIndex === 0) {
            hoverItemIndex = filteredItems.length - 1;
        } else {
            hoverItemIndex = hoverItemIndex + increment;
        }

        const hover = filteredItems[hoverItemIndex];

        if (hover && hover.selectable === false) {
            if (increment === 1 || increment === -1) setHoverIndex(increment);
            return;
        }
    }

    function isItemActive(item, value, itemId) {
        if (multiple) return;
        return value && value[itemId] === item[itemId];
    }

    function isItemFirst(itemIndex) {
        return itemIndex === 0;
    }

    function isItemSelectable(item) {
        return (item.groupHeader && item.selectable) || item.selectable || !item.hasOwnProperty('selectable');
    }

    const activeScroll = scrollAction;
    const hoverScroll = scrollAction;

    function scrollAction(node, args = { scroll: false }) {
        return {
            update(args) {
                if (args.scroll) {
                    handleListScroll();
                    node.scrollIntoView({ behavior: 'auto', block: 'nearest' });
                }
            },
        };
    }

    function setListWidth() {
        const { width } = container?.getBoundingClientRect() ?? { width: 0 };
        list!.style.width = listAutoWidth ? width + 'px' : 'auto';
    }

    let _floatingConfig = {
        strategy: strategy as 'absolute' | 'fixed',
        placement: placement as Placement,
        middleware: [offset(listOffset), flip(), shift()],
        autoUpdate: false,
    };

    const [floatingRef, floatingContent, floatingUpdate] = createFloatingActions(_floatingConfig as any);

    $: if (container && (floatingConfig as any)?.autoUpdate === undefined) {
        _floatingConfig.autoUpdate = true;
    }

    let prefloat = true;
    function listMounted(list, listOpen) {
        if (!list || !listOpen) return (prefloat = true);
        setTimeout(() => {
            prefloat = false;
        }, 0);
    }
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeyDown} />

<div
    class={twMerge('svelte-select bg-white font-normal text-gray-500', size === 'sm' ? 'svelte-select-sm' : 'svelte-select-md', $$props.class)}
    class:multi={multiple}
    class:disabled
    class:focused
    class:red={color === 'red'}
    class:list-open={listOpen}
    class:show-chevron={showChevron}
    class:error={hasError}
    style={containerStyles}
    on:pointerup|preventDefault={handleClick}
    bind:this={container}
    use:floatingRef
    role="none"
>
    {#if listOpen && !hideList}
        <div
            use:floatingContent
            bind:this={list}
            class="svelte-select-list"
            class:prefloat
            on:scroll={handleListScroll}
            on:pointerup|preventDefault|stopPropagation
            on:mousedown|preventDefault|stopPropagation
            role="none"
        >
            {#if $$slots['list-prepend']}<slot name="list-prepend" />{/if}
            {#if $$slots.list}<slot name="list" {filteredItems} />
            {:else if filteredItems.length > 0}
                {#each filteredItems as item, i}
                    <div
                        on:mouseover={() => handleHover(i)}
                        on:focus={() => handleHover(i)}
                        on:click|stopPropagation={() => handleItemClick({ item, i })}
                        on:keydown|preventDefault|stopPropagation
                        class="select-list-item"
                        tabindex="-1"
                        role="none"
                    >
                        <div
                            use:activeScroll={{ scroll: isItemActive(item, value, itemId) }}
                            use:hoverScroll={{ scroll: scrollToHoverItem === i }}
                            class="item"
                            class:list-group-title={item.groupHeader}
                            class:active={isItemActive(item, value, itemId)}
                            class:first={isItemFirst(i)}
                            class:hover={hoverItemIndex === i}
                            class:group-item={item.groupItem}
                            class:not-selectable={item?.selectable === false}
                        >
                            <div class="cursor-pointer truncate rounded-lg p-2 text-sm" class:font-semibold={value === item}>
                                <slot name="item" {item} index={i} checked={multiple ? value?.find(val => isEqual(val, item)) : isEqual(value, item)}>
                                    {#if checkable}
                                        <div class="pointer-events-none">
                                            <Checkbox checked={multiple ? value?.find(val => isEqual(val, item)) : isEqual(value, item)}>
                                                <p class="inline-block overflow-hidden text-ellipsis">{item.label}</p>
                                            </Checkbox>
                                        </div>
                                    {:else}
                                        {item.label}
                                    {/if}
                                </slot>
                            </div>
                        </div>
                    </div>
                {/each}
            {:else if !hideEmptyState}
                <slot name="empty">
                    <div class={twMerge('!m-0', 'p-2', $$props.listAutoWidth === false && 'w-[max-content]')}>{$_('packages.components.lib.basic.forms.svelteSelect.select.empty')}</div>
                </slot>
            {/if}
            {#if $$slots['list-append']}<slot name="list-append" />{/if}
        </div>
    {/if}

    <span aria-live="polite" aria-atomic="false" aria-relevant="additions text" class="a11y-text">
        {#if focused}
            <span id="aria-selection">{ariaSelection}</span>
            <span id="aria-context">
                {ariaContext}
            </span>
        {/if}
    </span>

    <div class="prepend">
        <slot name="prepend" />
    </div>

    <div class="value-container">
        {#if hasValue}
            {#if multiple}
                {#each value as item, i}
                    <div
                        class="multi-item"
                        class:active={activeValue === i}
                        class:disabled
                        on:click|preventDefault={() => (multiFullItemClearable ? handleMultiItemClear(i) : {})}
                        on:keydown|preventDefault|stopPropagation
                        role="none"
                    >
                        <span class="multi-item-text">
                            <slot name="selection" selection={item} index={i} {closeList}>
                                {#key `${item.value}-${i}`}
                                    <Badge
                                        class="max-w-full"
                                        color="gray"
                                        dismissable={!disabled}
                                        aria-disabled={disabled}
                                        on:dismiss={() => {
                                            if (disabled) return;
                                            value = dismissCriteria ? value.filter(v => dismissCriteria(v, item)) : value.filter(v => !isEqual(v, item));
                                            dispatch('change', value);

                                            closeList();
                                        }}
                                    >
                                        <span class="truncate">
                                            {item[label]}
                                        </span>
                                    </Badge>
                                {/key}
                            </slot>
                        </span>
                    </div>
                {/each}
            {:else}
                <div class="selected-item" class:hide-selected-item={hideSelectedItem}>
                    <slot name="selection" selection={value} {closeList}>
                        {value[label]}
                    </slot>
                </div>
            {/if}
        {/if}

        <input
            on:keydown={handleKeyDown}
            on:blur={handleBlur}
            on:focus={handleFocus}
            readOnly={!searchable}
            {..._inputAttributes}
            bind:this={input}
            bind:value={filterText}
            placeholder={placeholderText}
            style={inputStyles}
            {disabled}
        />
    </div>

    <div class="indicators" role="none" on:pointerup|preventDefault on:mousedown|preventDefault>
        {#if loading}
            <div class="icon loading" aria-hidden="true">
                <slot name="loading-icon">
                    <LoadingIcon />
                </slot>
            </div>
        {/if}

        {#if showClear}
            <button type="button" class="icon clear-select" on:click={handleClear}>
                <slot name="clear-icon">
                    <div class="cursor-pointer">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.5 4.5L13.5 13.5M4.5 13.5L13.5 4.5L4.5 13.5Z" stroke="#C7C9D1" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                </slot>
            </button>
        {/if}

        {#if showChevron}
            <div class="icon chevron" aria-hidden="true">
                <slot name="chevron-icon" {listOpen}>
                    <div class="{listOpen ? '' : 'rotate-180'} transition-transform">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 12L9 6L3 12" stroke="#636570" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                </slot>
            </div>
        {/if}
    </div>

    <slot name="input-hidden" {value}>
        <input {name} type="hidden" value={value ? JSON.stringify(value) : null} />
    </slot>

    {#if required && (!value || value.length === 0)}
        <slot name="required" {value}>
            <select class="required" required tabindex="-1" aria-hidden="true" />
        </slot>
    {/if}
</div>

<!--
  @component
  ## Feature
  [Go to SvelteSelect](https://github.com/rob-balfre/svelte-select)
  - Setup
  - SvelteSelect examples
  - github
  - Examples
  ## Props
  @prop items: any[] = []
  @prop value: any = null
  @prop placeholder: string = '선택해주세요'
  @prop disabled: boolean = false
  @prop multiple: boolean = false
  @prop label: string = 'label'
  @prop listOpen: boolean = false
  @prop justValue: unknown = undefined
  @prop filterText: string | undefined = undefined
  @prop dismissCriteria: ((a, b) => boolean) | undefined = undefined
  @prop color: 'base' | 'red' = 'base'
  @prop checkable: boolean = false
  @prop creatable: boolean = false

  ## Example
  ```
  <script>
    import { Select } from '@allibee/components/basic

    let value = null
  </script>

    <Select
        items={[
        { label: '선택1', value: 1 },
        { label: '선택2', value: 2 },
        { label: '선택3', value: 3 },
        ]}
        {value}
        placeholder="선택해주세요"
        disabled> 라벨 </Select>
  ```
-->

<style lang="postcss">
    .svelte-select {
        @apply border border-gray-200 bg-white;
        border-radius: var(--border-radius, 12px);
        min-height: var(--height, 48px);
        position: relative;
        display: flex;
        align-items: stretch;
        padding: var(--padding, var(--internal-padding, 0 14px));
        margin: var(--margin, 0);
        width: var(--width, 100%);
        font-size: var(--font-size, 14px);
        max-height: var(--max-height);
        cursor: pointer;
    }

    .svelte-select-md {
        --height: 48px;
        --border-radius: 12px;
        --internal-padding: 0 14px;
    }

    .svelte-select-sm {
        --height: 36px;
        --border-radius: 10px;
        --internal-padding: 6px 12px;
        --value-container-padding: 0;
        --chevron-height: 18px;
        --chevron-width: 18px;
        border: none;
        box-shadow: inset 0 0 0 1px theme('colors.gray-200');
    }

    .svelte-select-sm.focused {
        box-shadow: inset 0 0 0 1px var(--color-secondary-900, #001739);
    }

    .svelte-select-sm.disabled {
        box-shadow: inset 0 0 0 1px theme('colors.gray-200');
    }

    .svelte-select-sm input,
    .svelte-select-sm .selected-item {
        line-height: 20px;
    }

    .svelte-select.red {
        @apply border-red-500 bg-red-50;
    }

    * {
        box-sizing: var(--box-sizing, border-box);
    }

    .value-container {
        display: flex;
        flex: 1 1 0%;
        flex-wrap: wrap;
        align-items: center;
        gap: 5px 10px;
        padding: var(--value-container-padding, 5px 0);
        position: relative;
        overflow: var(--value-container-overflow, hidden);
        align-self: stretch;
    }

    .prepend,
    .indicators {
        display: flex;
        flex-shrink: 0;
        align-items: center;
    }

    .indicators {
        position: var(--indicators-position);
        top: var(--indicators-top);
        right: var(--indicators-right);
        bottom: var(--indicators-bottom);
    }

    input {
        @apply text-gray-900;
        position: absolute;
        cursor: default;
        border: none;
        padding: var(--input-padding, 0);
        letter-spacing: var(--input-letter-spacing, inherit);
        margin: var(--input-margin, 0);
        min-width: 10px;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: transparent;
        font-size: var(--font-size, 14px);
    }

    .red input {
        @apply text-red-900;
    }

    :not(.multi) > .value-container > input {
        width: 100%;
        height: 100%;
    }

    input::placeholder {
        color: var(--placeholder-color, theme('colors.gray.400'));
        opacity: var(--placeholder-opacity, 1);
    }

    input:focus {
        outline: none;
    }

    .svelte-select.focused {
        @apply border-secondary-900 border;
        border-radius: var(--border-radius-focused, var(--border-radius, 12px));
    }

    .svelte-select.focused.red {
        @apply border-red-500;
    }

    .disabled {
        background: var(--disabled-background, theme('colors.gray-200'));
        border-color: var(--disabled-border-color, theme('colors.gray-200'));
        color: var(--disabled-color, theme('colors.gray-400'));
        cursor: not-allowed;
    }

    .disabled input::placeholder {
        color: var(--disabled-placeholder-color, theme('colors.gray.500'));
        opacity: var(--disabled-placeholder-opacity, 1);
    }

    .selected-item {
        position: relative;
        overflow: var(--selected-item-overflow, visible);
        padding: var(--selected-item-padding, 0);
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--selected-item-color, theme('colors.gray.900'));
        font-size: var(--font-size, 14px);
    }

    .multi .selected-item {
        position: absolute;
        line-height: var(--height, 48px);
        height: var(--height, 48px);
    }

    .selected-item:focus {
        outline: none;
    }

    .hide-selected-item {
        opacity: 0;
    }

    .icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .clear-select {
        all: unset;
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--clear-select-width, 18px);
        height: var(--clear-select-height, 100%);
        color: var(--clear-select-color, var(--icons-color));
        margin: var(--clear-select-margin, 0 0 0 8px);
        pointer-events: all;
        flex-shrink: 0;
    }

    .clear-select:focus {
        outline: var(--clear-select-focus-outline, 1px solid #006fe8);
    }

    .loading {
        width: var(--loading-width, 40px);
        height: var(--loading-height);
        color: var(--loading-color, var(--icons-color));
        margin: var(--loading--margin, 0);
        flex-shrink: 0;
    }

    .chevron {
        width: var(--chevron-width, 40px);
        height: var(--chevron-height, 40px);
        background: var(--chevron-background, transparent);
        pointer-events: var(--chevron-pointer-events, none);
        color: var(--chevron-color, var(--icons-color));
        border: var(--chevron-border, 0 0 0 1px solid #d8dbdf);
        flex-shrink: 0;
    }

    .multi {
        padding: var(--multi-select-padding, var(--internal-padding, 0 0 0 16px));
    }

    .multi input {
        padding: var(--multi-select-input-padding, 0);
        position: relative;
        margin: var(--multi-select-input-margin, 0);
        flex: 1 1 40px;
    }

    .svelte-select.error {
        border: var(--error-border, 1px solid #ff2d55);
        background: var(--error-background, #fff);
    }

    .a11y-text {
        z-index: 9999;
        border: 0px;
        clip: rect(1px, 1px, 1px, 1px);
        height: 1px;
        width: 1px;
        position: absolute;
        overflow: hidden;
        padding: 0px;
        white-space: nowrap;
    }

    .multi-item {
        background: var(--multi-item-bg, transparent);
        margin: var(--multi-item-margin, 0);
        outline: var(--multi-item-outline, none);
        border-radius: var(--multi-item-border-radius, 0);
        height: var(--multi-item-height, 1);
        line-height: var(--multi-item-height, 1);
        display: flex;
        cursor: default;
        padding: var(--multi-item-padding, 0);
        overflow: hidden;
        gap: var(--multi-item-gap, 0);
        outline-offset: -1px;
        max-width: var(--multi-max-width, none);
        color: var(--multi-item-color, var(--item-color, theme('colors.gray.900')));
    }

    .multi-item.disabled:hover {
        background: var(--multi-item-disabled-hover-bg, theme('colors.gray-200'));
        color: var(--multi-item-disabled-hover-color, theme('colors.gray-400'));
    }

    .multi-item-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .multi-item-clear {
        display: flex;
        align-items: center;
        justify-content: center;
        --clear-icon-color: var(--multi-item-clear-icon-color, #000);
    }

    .multi-item.active {
        outline: var(--multi-item-active-outline, 1px solid #006fe8);
    }

    .svelte-select-list {
        box-shadow: var(--list-shadow, 0px 0px 3px 0px rgba(0, 0, 0, 0.1), 0px 0px 2px -1px rgba(0, 0, 0, 0.1));
        border-radius: var(--list-border-radius, 8px);
        max-height: var(--list-max-height, 269px);
        overflow-y: auto;
        background: var(--list-background, theme('colors.gray.50'));
        position: var(--list-position, absolute);
        z-index: var(--list-z-index, 2);
        border: var(--list-border, 1px solid var(--list-border-color, theme('colors.gray.200')));
    }

    .prefloat {
        opacity: 0;
        pointer-events: none;
    }

    .list-group-title {
        color: var(--group-title-color, #8f8f8f);
        cursor: default;
        font-size: var(--group-title-font-size, 16px);
        font-weight: var(--group-title-font-weight, 600);
        height: var(--height, 48px);
        line-height: var(--height, 48px);
        padding: var(--group-title-padding, 0 20px);
        text-overflow: ellipsis;
        overflow-x: hidden;
        white-space: nowrap;
        text-transform: var(--group-title-text-transform, uppercase);
        border-width: var(--group-title-border-width, medium);
        border-style: var(--group-title-border-style, none);
        border-color: var(--group-title-border-color, color);
    }

    .empty {
        text-align: var(--list-empty-text-align, center);
        padding: var(--list-empty-padding, 20px 0);
        color: var(--list-empty-color, #78848f);
    }

    .item {
        cursor: default;
        height: var(--item-height, 37px);
        line-height: var(--item-line-height, var(--height, 48px));
        padding: var(--item-padding, 0);
        color: var(--item-color, theme('colors.gray.900'));
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        transition: var(--item-transition, all 0.2s);
        align-items: center;
        width: 100%;
    }

    .item.group-item {
        padding-left: var(--group-item-padding-left, 40px);
    }

    .item:active {
        background: var(--item-active-background, #b9daff);
    }

    .item.active {
        background: var(--item-is-active-bg, theme('colors.gray.300'));
        color: var(--item-is-active-color, theme('colors.gray.900'));
    }

    .item.first {
        border-radius: var(--item-first-border-radius, 8px);
    }

    .item.hover:not(.active) {
        background: var(--item-hover-bg, theme('colors.gray.200'));
        color: var(--item-hover-color, theme('colors.gray.900'));
    }

    .item.not-selectable,
    .item.hover.item.not-selectable,
    .item.active.item.not-selectable,
    .item.not-selectable:active {
        color: var(--item-is-not-selectable-color, theme('colors.gray.300'));
        background: transparent;
    }

    .required {
        opacity: 0;
        z-index: -1;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
    }

    .svelte-select .select-list-item {
        margin: var(--list-item-margin, 0.5rem);
    }
    .svelte-select .item {
        @apply rounded-lg;
    }
    .svelte-select .item.active {
        @apply font-semibold;
    }
    .svelte-select .item:hover {
        @apply font-normal;
    }

    .svelte-select .value-container {
        @apply leading-none;
        gap: var(--value-container-gap, 0.5rem) !important;
        flex-wrap: var(--value-container-flex-wrap, wrap) !important;
    }

    .svelte-select .value-container .selected-item {
        @apply !line-clamp-1 w-full max-w-full !whitespace-normal !break-all leading-normal;
    }

    .svelte-select .indicators {
        @apply h-max;
        padding-right: var(--indicators-padding-right, 0);
        /* chevron 가운데 정렬 */
        align-self: center;
    }

    .svelte-select .indicators .chevron {
        @apply !w-4;
        @apply !ml-2;
    }
</style>
